import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getVideoById, getRelatedVideos, categories } from '@/mocks/videos';
import type { VideoArticle } from '@/mocks/videos';

const siteUrl = 'https://xiuxiushipin.com';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [video, setVideo] = useState<VideoArticle | undefined>();
  const [related, setRelated] = useState<VideoArticle[]>([]);
  const [likeCount, setLikeCount] = useState(0);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    if (id) {
      const found = getVideoById(id);
      setVideo(found);
      if (found) {
        setLikeCount(found.likeCount);
        setRelated(getRelatedVideos(id, 4));
      }
    }
  }, [id]);

  if (!video) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl text-foreground-900 mb-2">视频未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">该视频可能已被移除或链接无效</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === video.categorySlug);

  const articleSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: video.title,
      description: video.description,
      image: video.coverImage,
      author: {
        '@type': 'Person',
        name: video.author,
      },
      datePublished: video.publishDate,
      publisher: {
        '@type': 'Organization',
        name: '羞羞视频精选合集',
        url: siteUrl,
      },
      url: `${siteUrl}/article/${video.id}`,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${siteUrl}/article/${video.id}`,
      },
    },
  ];

  const handleLike = () => {
    if (!liked) {
      setLikeCount((prev) => prev + 1);
      setLiked(true);
    }
  };

  return (
    <>
      <SEO
        title={video.title}
        description={video.description}
        keywords={`${video.title},${video.category},羞羞视频,${video.tags.join(',')}`}
        canonicalUrl={`${siteUrl}/article/${video.id}`}
        ogType="article"
        ogImage={video.coverImage}
        schemaData={articleSchema}
      />

      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 bg-background-50">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: video.category, path: `/category/${video.categorySlug}` },
              { label: video.title },
            ]}
          />
        </div>

        <article className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Link
                  to={`/category/${video.categorySlug}`}
                  className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap cursor-pointer"
                >
                  {video.category}
                </Link>
                {video.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-background-100 text-foreground-500 text-xs whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
              <h1 className="font-heading text-xl md:text-3xl text-foreground-900 mb-4 leading-tight">
                {video.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {video.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {video.publishDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {video.viewCount >= 10000 ? `${(video.viewCount / 10000).toFixed(1)}万次播放` : `${video.viewCount}次播放`}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  时长 {video.duration}
                </span>
              </div>
            </header>

            {/* Cover Image */}
            <div className="relative aspect-video rounded-2xl overflow-hidden mb-10 bg-background-100">
              <img
                src={video.coverImage}
                alt={video.title}
                className="w-full h-full object-cover"
                title={`羞羞视频 - ${video.title}`}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center rounded-full bg-primary-500/90 hover:bg-primary-600 cursor-pointer transition-all duration-300 hover:scale-110 shadow-xl shadow-primary-500/40">
                  <i className="ri-play-fill text-3xl md:text-4xl text-background-50 ml-1"></i>
                </div>
              </div>
              <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-foreground-900/80 backdrop-blur-sm rounded-lg text-background-50 text-sm font-mono">
                {video.duration}
              </div>
            </div>

            {/* Content */}
            <div
              className="prose prose-sm md:prose-base max-w-none mb-10 text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: video.contentHtml }}
            />

            {/* Actions */}
            <div className="flex items-center gap-4 py-6 border-t border-b border-background-200/70 mb-10">
              <button
                onClick={handleLike}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  liked
                    ? 'bg-primary-100 text-primary-600'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500'
                }`}
              >
                <i className={`${liked ? 'ri-heart-fill' : 'ri-heart-line'} text-lg`}></i>
                {likeCount >= 10000 ? `${(likeCount / 10000).toFixed(1)}万` : likeCount}
              </button>
              <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200/70 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-share-forward-line text-lg"></i>
                分享
              </button>
              <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200/70 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-bookmark-line text-lg"></i>
                收藏
              </button>
            </div>

            {/* Author Card */}
            <div className="flex items-center gap-4 p-5 rounded-2xl bg-background-100 mb-12">
              <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-user-line text-xl text-primary-600"></i>
              </div>
              <div>
                <div className="font-medium text-foreground-900 text-sm">{video.author}</div>
                <p className="text-foreground-500 text-xs">视频创作者 · 用心记录生活中的美好瞬间</p>
              </div>
            </div>

            {/* Related Videos */}
            {related.length > 0 && (
              <section>
                <h3 className="font-heading text-xl text-foreground-900 mb-6">
                  <i className="ri-links-line mr-2 text-primary-500"></i>
                  相关推荐
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {related.map((v) => (
                    <Link
                      key={v.id}
                      to={`/article/${v.id}`}
                      className="group flex gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer"
                    >
                      <div className="w-40 md:w-48 aspect-video rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={v.coverImage}
                          alt={v.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          title={`羞羞视频 - ${v.title}`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-foreground-900 text-sm line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug mb-1.5">
                          {v.title}
                        </h4>
                        <p className="text-foreground-500 text-xs line-clamp-2 mb-2 leading-relaxed">
                          {v.description}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-foreground-400">
                          <span>{v.category}</span>
                          <span>{v.publishDate}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}